import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Package, Users, Store, ShoppingBag, Star, Menu,
  Tag, ChevronLeft, PhoneCall, LayoutGrid, Wallet, Activity,
  FileText, Receipt, CreditCard, TrendingUp, TrendingDown,
  ClipboardList, BookOpen, Building2, FileCheck, BarChart2,
  Settings, AlertCircle, ChevronDown, ChevronRight, Layers,
  ArrowUpDown, FileDown, Shield, Calculator, BookMarked
} from "lucide-react";

const SIDEBAR_TIMEOUT_MS = 60000;

// ─── Nav Structure ─────────────────────────────────────────────────────────────
const navGroups = [
  {
    id: 'main',
    label: null, // no section header
    items: [
      { id: 'dashboard', path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    ]
  },
  {
    id: 'billing',
    label: 'BILLING',
    icon: FileText,
    collapsible: true,
    items: [
      { id: 'billing-register', path: '/admin/billing', icon: ClipboardList, label: 'Invoice Register', exact: true },
      { id: 'sales-new',       path: '/admin/billing/new/sales',            icon: TrendingUp,    label: 'Sales Invoice' },
      { id: 'purchase-new',    path: '/admin/billing/new/purchase',         icon: TrendingDown,  label: 'Purchase Bill' },
      { id: 'sales-return',    path: '/admin/billing/new/sales_return',     icon: ArrowUpDown,   label: 'Sales Return' },
      { id: 'purchase-return', path: '/admin/billing/new/purchase_return',  icon: ArrowUpDown,   label: 'Purchase Return' },
      { id: 'credit-note',     path: '/admin/billing/new/credit_note',      icon: FileCheck,     label: 'Credit Note' },
      { id: 'debit-note',      path: '/admin/billing/new/debit_note',       icon: FileCheck,     label: 'Debit Note' },
      { id: 'quotation',       path: '/admin/billing/new/quotation',        icon: FileText,      label: 'Quotation' },
      { id: 'proforma',        path: '/admin/billing/new/proforma',         icon: FileText,      label: 'Proforma Invoice' },
      { id: 'delivery-challan',path: '/admin/billing/new/delivery_challan', icon: FileDown,      label: 'Delivery Challan' },
      { id: 'receipt',         path: '/admin/billing/new/receipt',          icon: Receipt,       label: 'Receipt Voucher' },
      { id: 'payment-voucher', path: '/admin/billing/new/payment',         icon: CreditCard,    label: 'Payment Voucher' },
      { id: 'journal',         path: '/admin/billing/new/journal',          icon: BookOpen,      label: 'Journal Entry' },
    ]
  },
  {
    id: 'accounts',
    label: 'ACCOUNTS',
    icon: BookMarked,
    collapsible: true,
    items: [
      { id: 'parties',       path: '/admin/parties',      icon: Building2,    label: 'Parties (Cust/Supp)' },
      { id: 'receivables',   path: '/admin/receivables',  icon: TrendingUp,   label: 'Receivables' },
      { id: 'payables',      path: '/admin/payables',     icon: TrendingDown, label: 'Payables' },
    ]
  },
  {
    id: 'gst',
    label: 'GST',
    icon: Shield,
    collapsible: true,
    items: [
      { id: 'gst-dashboard',    path: '/admin/gst',                  icon: BarChart2,    label: 'GST Dashboard' },
      { id: 'gstr1',            path: '/admin/gst/gstr1',            icon: FileText,     label: 'GSTR-1' },
      { id: 'gstr3b',           path: '/admin/gst/gstr3b',           icon: FileCheck,    label: 'GSTR-3B' },
      { id: 'gstr2ab',          path: '/admin/gst/gstr2ab',          icon: FileDown,     label: 'GSTR-2A / 2B' },
      { id: 'reconciliation',   path: '/admin/gst/reconciliation',   icon: ArrowUpDown,  label: 'Reconciliation' },
      { id: 'itc',              path: '/admin/gst/itc',              icon: Calculator,   label: 'ITC Management' },
      { id: 'gst-reports',      path: '/admin/gst/reports',          icon: BarChart2,    label: 'GST Reports' },
      { id: 'gst-settings',     path: '/admin/gst/settings',         icon: Settings,     label: 'GST Settings' },
    ]
  },
  {
    id: 'store',
    label: 'STORE',
    items: [
      { id: 'products',          path: '/admin/products',          icon: Package,      label: 'Products' },
      { id: 'orders',            path: '/admin/orders',            icon: ShoppingBag,  label: 'Orders' },
      { id: 'coupons',           path: '/admin/coupons',           icon: Tag,          label: 'Coupons' },
      { id: 'reviews',           path: '/admin/reviews',           icon: Star,         label: 'Reviews' },
    ]
  },
  {
    id: 'operations',
    label: 'OPERATIONS',
    items: [
      { id: 'users',             path: '/admin/users',             icon: Users,        label: 'Users' },
      { id: 'vendors',           path: '/admin/vendors',           icon: Store,        label: 'Vendors' },
      { id: 'expenses',          path: '/admin/expenses',          icon: Wallet,       label: 'Expense Tracker' },
      { id: 'service-requests',  path: '/admin/service-requests',  icon: Star,         label: 'Service Requests' },
      { id: 'service-reminders', path: '/admin/service-reminders', icon: Star,         label: 'Service Reminders' },
      { id: 'phonebook',         path: '/admin/phonebook',         icon: PhoneCall,    label: 'Customer Phonebook' },
      { id: 'zones',             path: '/admin/zones',             icon: LayoutGrid,   label: 'Zones' },
      { id: 'job-monitor',       path: '/admin/job-monitor',       icon: Activity,     label: 'Job Monitor' },
    ]
  }
];

// ─── NavItem ────────────────────────────────────────────────────────────────────
function NavItem({ item, location, navigate, closeSidebar }) {
  const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path) && item.path !== '/admin';
  const isMainActive = item.exact && location.pathname === item.path;
  const active = item.exact ? isMainActive : isActive;

  return (
    <button
      onClick={() => { navigate(item.path); if (window.innerWidth < 1024) closeSidebar(); }}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all duration-100 text-xs whitespace-nowrap
        ${active
          ? 'bg-indigo-600 text-white font-bold shadow-sm shadow-indigo-600/20'
          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 font-medium'}`}>
      <item.icon size={14} className="shrink-0 opacity-80" />
      <span className="truncate">{item.label}</span>
    </button>
  );
}

// ─── NavGroup ──────────────────────────────────────────────────────────────────
function NavGroup({ group, location, navigate, closeSidebar }) {
  const isGroupActive = group.items.some(item => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  });
  const [open, setOpen] = useState(isGroupActive || !group.collapsible);

  useEffect(() => { if (isGroupActive) setOpen(true); }, [location.pathname]);

  if (!group.label) {
    return (
      <div className="space-y-0.5">
        {group.items.map(item => <NavItem key={item.id} item={item} location={location} navigate={navigate} closeSidebar={closeSidebar} />)}
      </div>
    );
  }

  return (
    <div>
      {group.collapsible ? (
        <button onClick={() => setOpen(v => !v)}
          className={`w-full flex items-center justify-between px-2 py-1.5 mb-0.5 rounded-lg transition-colors
            ${isGroupActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}>
          <div className="flex items-center gap-2">
            {group.icon && <group.icon size={12} className="shrink-0" />}
            <span className="text-[10px] font-black tracking-widest uppercase">{group.label}</span>
          </div>
          {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </button>
      ) : (
        <div className="flex items-center gap-2 px-2 py-1.5 mb-0.5">
          {group.icon && <group.icon size={12} className="shrink-0 text-slate-500" />}
          <span className="text-[10px] font-black tracking-widest uppercase text-slate-500">{group.label}</span>
        </div>
      )}

      {(!group.collapsible || open) && (
        <div className={`space-y-0.5 ${group.collapsible ? 'ml-1 pl-2 border-l border-slate-700/50' : ''}`}>
          {group.items.map(item => <NavItem key={item.id} item={item} location={location} navigate={navigate} closeSidebar={closeSidebar} />)}
        </div>
      )}
    </div>
  );
}

// ─── Main AdminSidebar ─────────────────────────────────────────────────────────
export default function AdminSidebar() {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [isOpen, setIsOpen] = useState(true);
  const panelRef  = useRef(null);
  const timerRef  = useRef(null);

  const startTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsOpen(false), SIDEBAR_TIMEOUT_MS);
  }, []);
  const stopTimer = () => clearTimeout(timerRef.current);

  useEffect(() => { if (isOpen) startTimer(); return stopTimer; }, [isOpen, startTimer]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); else startTimer(); };
    const onMouse = (e) => {
      if (window.innerWidth < 1024 && panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouse);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onMouse); };
  }, [isOpen, startTimer]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased flex">
      {/* Mobile Backdrop */}
      <div className={`fixed inset-0 bg-slate-950/20 backdrop-blur-sm z-40 transition-opacity duration-300 lg:hidden
        ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsOpen(false)} />

      {/* Sidebar */}
      <aside ref={panelRef} onMouseMove={startTimer} onScroll={startTimer}
        className={`fixed top-0 left-0 h-screen bg-slate-900 text-slate-400 z-50 flex flex-col
          transition-all duration-300 ease-in-out border-r border-slate-800 shadow-2xl shrink-0 lg:sticky lg:top-0
          ${isOpen ? 'w-56 translate-x-0' : 'w-56 -translate-x-full lg:translate-x-0 lg:w-0 lg:border-r-0'}`}>

        {/* Brand Header */}
        <div className={`h-14 flex items-center justify-between px-4 border-b border-slate-800/60 shrink-0 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center shrink-0">
              <span className="text-white font-black text-xs">P</span>
            </div>
            <div>
              <span className="font-black text-white text-sm">ProNest</span>
              <span className="block text-[9px] text-slate-500 font-bold uppercase tracking-widest">Admin</span>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)}
            className="p-1 rounded-lg border border-slate-800 hover:bg-slate-800 text-slate-500 hover:text-white transition-all">
            <ChevronLeft size={14} />
          </button>
        </div>

        {/* Scrollable Nav */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-none">
          {navGroups.map(group => (
            <NavGroup key={group.id} group={group} location={location} navigate={navigate} closeSidebar={() => setIsOpen(false)} />
          ))}
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-h-screen flex flex-col min-w-0 transition-all duration-300 ease-in-out">
        {/* Top Header */}
        <header className="h-14 border-b border-slate-200/60 bg-white sticky top-0 z-30 shrink-0 w-full flex items-center">
          <div className="w-full px-5 flex items-center gap-3">
            {!isOpen && (
              <button onClick={() => setIsOpen(true)}
                className="p-1.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all shadow-sm shrink-0">
                <Menu size={16} />
              </button>
            )}
            <h1 className="text-sm font-bold text-slate-800 tracking-wide">ProNest Management Console</h1>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:block">
                {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="w-full px-4 sm:px-6 py-5 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}